import { Card, cn } from '@/design-system';
import type { Order, OrderAccess } from '@/entities/order';
import { formatDayTimeShort } from '@/shared/lib';

import { inspectionsCopy } from '../copy';
import { OrderStatusPill } from './OrderStatusPill';
import { OrderSurveyAction } from './SurveyActionButton';

export interface OrderCardsProps {
    orders: readonly Order[];
    selectedUuid: string | null;
    access: OrderAccess;
    onSelect: (order: Order) => void;
}

/**
 * De werklijst op een telefoon: geen tabel maar een stapel kaarten, één kolom.
 *
 * Zeven kolommen van samen 940 px passen niet op 390 px, en een tabel die horizontaal
 * schuift is op een telefoon geen lijst maar een puzzel. Dus krijgt elke keuring een
 * kaart met wat je onderweg nodig hebt — adres, status, wanneer, wie — en daaronder de
 * ene knop die er bij hoort, over de volle breedte.
 *
 * De kaart is geen knop: de kop erin opent de detailbalk, de knop eronder start. Twee
 * aanraakvlakken van elk minstens 44 px, en geen van de twee doet per ongeluk het andere.
 */
export function OrderCards({ orders, selectedUuid, access, onSelect }: OrderCardsProps) {
    return (
        <ul aria-label={inspectionsCopy.tableLabel} className="m-0 flex list-none flex-col gap-2 p-4">
            {orders.map((order) => (
                <li key={order.uuid}>
                    <Card
                        padding="md"
                        radius={9}
                        className={cn(
                            'flex flex-col gap-3',
                            order.uuid === selectedUuid &&
                                'bg-[var(--accent-tint)] shadow-[inset_2px_0_0_var(--accent)]',
                        )}
                    >
                        <button
                            type="button"
                            onClick={() => onSelect(order)}
                            className="-m-1 flex min-h-[44px] cursor-pointer flex-col items-start gap-[6px] border-0 bg-transparent p-1 text-left"
                        >
                            <span className="font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                                {order.property.shortAddress}
                            </span>
                            <span className="flex flex-wrap items-center gap-2">
                                <OrderStatusPill order={order} />
                                <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                                    {order.scheduledAt
                                        ? formatDayTimeShort(order.scheduledAt)
                                        : inspectionsCopy.noDate}
                                </span>
                            </span>
                            <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                                {[
                                    order.number,
                                    order.serviceLabel,
                                    order.assignee?.name ?? inspectionsCopy.noInspector,
                                ].join(' · ')}
                            </span>
                        </button>

                        <OrderSurveyAction order={order} access={access} fullWidth />
                    </Card>
                </li>
            ))}
        </ul>
    );
}
